import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePaymentErrorHandler } from "@/hooks/usePaymentErrorHandler";
import { parsePaymentErrorFromInvokeError } from "@/lib/quotaError";

// Mirror reaction to a saved body measurement — mirrors useWorkoutReact
// exactly (invoke body-react, cache-first server-side). 402 (Free tier —
// Fitness reactions are Premium+) resolves silently to no reaction.

export interface BodyReactResult {
  message: string;
  cached: boolean;
}

// useLanguage lang ("PT-BR" | "EN" | "NL") → react-function language codes.
export function reactLanguageFor(lang: string): string {
  if (lang === "EN") return "en";
  if (lang === "NL") return "nl";
  return "pt-BR";
}

export const useBodyReact = () => {
  const [reaction, setReaction] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [identityVersion, setIdentityVersion] = useState(0);
  const mounted = useRef(false), generation = useRef(0), authEpoch = useRef(0);
  const owner = useRef<string | null | undefined>(undefined);
  const active = useRef<AbortController | null>(null);
  const handlePaymentError = usePaymentErrorHandler();
  useEffect(() => {
    mounted.current = true;
    setIdentityVersion(authEpoch.current);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (next === owner.current) return;
      owner.current = next; authEpoch.current++; generation.current++;
      active.current?.abort(); active.current = null;
      setReaction(null); setIsLoading(false); setIdentityVersion(authEpoch.current);
    });
    const lifecycle = { mounted, authEpoch, generation, active };
    return () => {
      lifecycle.mounted.current = false; lifecycle.authEpoch.current++; lifecycle.generation.current++;
      lifecycle.active.current?.abort(); lifecycle.active.current = null; subscription.unsubscribe();
    };
  }, []);

  const react = useCallback(async (measurementId: string, language: string) => {
    // A retained callback belongs to the identity render that created it.
    // A→B→A does not revive an earlier request or a closed result surface.
    if (!mounted.current || identityVersion !== authEpoch.current) return;
    const ticket = ++generation.current, epoch = authEpoch.current;
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    const current = () => mounted.current && ticket === generation.current && epoch === authEpoch.current && !controller.signal.aborted;
    setIsLoading(true);
    setReaction(null);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (!current()) return;
      if (authError || !user) throw new Error("not_authenticated");
      if (owner.current === undefined) owner.current = user.id;
      if (owner.current !== user.id) return;
      const expectedOwner = user.id;
      const { data, error: fnError } = await supabase.functions.invoke("body-react", {
        body: { measurement_id: measurementId, language },
        signal: controller.signal,
      });
      if (!current() || owner.current !== expectedOwner) return;
      if (fnError) {
        const paymentError = await parsePaymentErrorFromInvokeError(fnError);
        if (!current() || owner.current !== expectedOwner) return;
        if (paymentError?.type === "module_locked") return;
        if (paymentError) { handlePaymentError(paymentError); return; }
        // Free tier gets a 402 module lock — expected, no reaction shown.
        console.warn("[useBodyReact] no reaction", fnError);
        return;
      }
      const result = data as BodyReactResult;
      if (typeof result?.message !== "string" || !result.message.trim() || result.message.length > 2000 || typeof result.cached !== "boolean") {
        throw new Error("reaction_unconfirmed");
      }
      setReaction(result.message);
    } catch (err) {
      if (current()) console.error("[useBodyReact] failed", err);
    } finally {
      if (current()) { active.current = null; setIsLoading(false); }
    }
  }, [identityVersion, handlePaymentError]);

  const clear = useCallback(() => {
    generation.current++; active.current?.abort(); active.current = null;
    if (mounted.current) { setReaction(null); setIsLoading(false); }
  }, []);

  return { reaction, isLoading, react, clear };
};
